import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { maintenanceApi, MaintDevice } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import DeviceForm from "./DeviceForm";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";

const MaintenanceDevices: React.FC = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<MaintDevice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      const res = await maintenanceApi.getDevices(params);
      setDevices(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchDevices();
  };

  const handleCreated = () => {
    setShowCreateForm(false);
    setPage(1);
    fetchDevices();
  };

  return (
    <MaintenanceLayout title="Devices (Maintenance)">
      <div className="maint-stations">
        <div className="maint-toolbar">
          <form onSubmit={handleSearchSubmit} className="maint-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search devices, orgs, or stations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
          {canEditMaintenance() && (
            <button className="maint-btn-primary" onClick={() => setShowCreateForm(true)}>
              <Plus size={16} /> Add Device
            </button>
          )}
        </div>

        {showCreateForm && (
          <DeviceForm onClose={() => setShowCreateForm(false)} onCreated={handleCreated} />
        )}

        {loading ? (
          <p className="maint-loading">Loading...</p>
        ) : (
          <>
            <table className="maint-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Display Name</th>
                  <th>Station</th>
                  <th>Organization</th>
                  <th>OCPP Ver</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} onClick={() => navigate(`/maintenance/devices/${d.id}`)} className="clickable">
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {d.device_logo_url && (
                          <img src={d.device_logo_url} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }} />
                        )}
                        {d.unique_name || `Device #${d.id}`}
                      </div>
                    </td>
                    <td>{d.display_name || "-"}</td>
                    <td>{d.station_name || "-"}</td>
                    <td>{d.org_name || "-"}</td>
                    <td>{d.ocpp_version || "-"}</td>
                    <td>
                      <span className={`maint-status maint-status-${d.device_status}`}>
                        {d.device_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {devices.length === 0 && (
                  <tr><td colSpan={6} className="maint-empty">No devices found</td></tr>
                )}
              </tbody>
            </table>

            <div className="maint-pagination">
              <span>{total} devices total</span>
              <div className="maint-pagination-controls">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={16} /></button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={16} /></button>
              </div>
            </div>
          </>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default MaintenanceDevices;
